import React, { useState, useMemo, useCallback } from 'react';
import { MagnifyingGlass, CaretDown, CaretRight, Buildings, House, Package, SpinnerGap, CheckSquare, Square } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AssetScope, Asset } from 'shared/types/asset';
import LocationItem, { LocationItemData } from './DraggableLocationItem';

interface Building {
  id: string;
  company?: { name: string };
}

interface Unit {
  id: string;
  buildingId: string;
  unitType?: string;
}

interface LocationSourcePanelProps {
  scopes: AssetScope[];
  buildings: Building[];
  units: Unit[];
  existingAssets: Asset[];
  onBulkCreate: (items: LocationItemData[]) => void;
  isCreating: boolean;
}

const LocationSourcePanel: React.FC<LocationSourcePanelProps> = ({
  scopes,
  buildings,
  units,
  existingAssets,
  onBulkCreate,
  isCreating,
}) => {
  const [search, setSearch] = useState('');
  const [expandedBuildings, setExpandedBuildings] = useState<Set<string>>(new Set());
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());

  const toggleBuilding = (buildingId: string) => {
    setExpandedBuildings((prev) => {
      const next = new Set(prev);
      if (next.has(buildingId)) {
        next.delete(buildingId);
      } else {
        next.add(buildingId);
      }
      return next;
    });
  };

  const toggleSelection = useCallback((itemId: string, checked: boolean) => {
    setSelectedLocations((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });
  }, []);

  // Count existing instances per building/unit
  const instanceCounts = useMemo(() => {
    const buildingCounts: Record<string, number> = {};
    const unitCounts: Record<string, number> = {};

    for (const asset of existingAssets) {
      if (asset.buildingId) {
        buildingCounts[asset.buildingId] = (buildingCounts[asset.buildingId] || 0) + 1;
      }
      if (asset.unitId) {
        unitCounts[asset.unitId] = (unitCounts[asset.unitId] || 0) + 1;
      }
    }

    return { buildingCounts, unitCounts };
  }, [existingAssets]);

  // Filter buildings/units by search
  const filteredBuildings = useMemo(() => {
    if (!search.trim()) return buildings;
    const s = search.toLowerCase();
    return buildings.filter(
      (b) =>
        b.id.toLowerCase().includes(s) ||
        b.company?.name?.toLowerCase().includes(s)
    );
  }, [buildings, search]);

  const getUnitsForBuilding = (buildingId: string) => {
    const filtered = units.filter((u) => u.buildingId === buildingId);
    if (!search.trim()) return filtered;
    const s = search.toLowerCase();
    return filtered.filter(
      (u) =>
        u.id.toLowerCase().includes(s) ||
        u.unitType?.toLowerCase().includes(s)
    );
  };

  // Build all visible items for select-all logic
  const allVisibleItems = useMemo(() => {
    const items: LocationItemData[] = [];

    if (scopes.includes(AssetScope.BUILDING)) {
      for (const b of filteredBuildings) {
        items.push({
          id: `building-${b.id}`,
          type: 'building',
          label: b.id,
          sublabel: b.company?.name,
          buildingId: b.id,
          instanceCount: instanceCounts.buildingCounts[b.id] || 0,
        });
      }
    }

    if (scopes.includes(AssetScope.UNIT) || scopes.includes(AssetScope.ROOM)) {
      for (const b of filteredBuildings) {
        const buildingUnits = getUnitsForBuilding(b.id);
        for (const u of buildingUnits) {
          items.push({
            id: `unit-${u.id}`,
            type: 'unit',
            label: u.id,
            sublabel: u.unitType,
            buildingId: b.id,
            unitId: u.id,
            instanceCount: instanceCounts.unitCounts[u.id] || 0,
          });
        }
      }
    }

    return items;
  }, [scopes, filteredBuildings, units, search, instanceCounts]);

  const allSelected = allVisibleItems.length > 0 && allVisibleItems.every((item) => selectedLocations.has(item.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedLocations(new Set());
    } else {
      setSelectedLocations(new Set(allVisibleItems.map((item) => item.id)));
    }
  };

  const handleBulkCreate = () => {
    const items = allVisibleItems.filter((item) => selectedLocations.has(item.id));
    if (items.length === 0) return;
    onBulkCreate(items);
    setSelectedLocations(new Set());
  };

  const selectedCount = allVisibleItems.filter((item) => selectedLocations.has(item.id)).length;

  // PORTABLE scope only: just a button
  if (scopes.length === 1 && scopes[0] === AssetScope.PORTABLE) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Package className="h-4 w-4" />
            Locaties
          </h3>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              Draagbare voorzieningen zijn niet aan een locatie gekoppeld.
            </p>
            <Button
              onClick={() =>
                onBulkCreate([{
                  id: 'portable',
                  type: 'building',
                  label: 'Draagbaar',
                  buildingId: '',
                  instanceCount: existingAssets.length,
                }])
              }
              disabled={isCreating}
            >
              {isCreating ? (
                <SpinnerGap className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Draagbare voorziening aanmaken
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const showBuildingSection = scopes.includes(AssetScope.BUILDING);
  const showUnitSection = scopes.includes(AssetScope.UNIT) || scopes.includes(AssetScope.ROOM);

  return (
    <div className="flex flex-col h-full">
      {/* Header with search and action bar */}
      <div className="p-4 border-b space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Buildings className="h-4 w-4" />
            Locaties
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={handleSelectAll}
            >
              {allSelected ? (
                <CheckSquare className="h-3.5 w-3.5" />
              ) : (
                <Square className="h-3.5 w-3.5" />
              )}
              {allSelected ? 'Deselecteren' : 'Alles selecteren'}
            </Button>
          </div>
        </div>
        <div className="relative">
          <MagnifyingGlass className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek gebouwen of units..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="px-4 py-2 border-b bg-primary/5 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedCount} unit{selectedCount !== 1 ? 's' : ''} geselecteerd
          </span>
          <Button
            size="sm"
            onClick={handleBulkCreate}
            disabled={isCreating}
          >
            {isCreating ? (
              <SpinnerGap className="h-4 w-4 animate-spin mr-1" />
            ) : null}
            Aanmaken voor {selectedCount} unit{selectedCount !== 1 ? 's' : ''}
          </Button>
        </div>
      )}

      {/* Location list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {/* BUILDING scope section */}
        {showBuildingSection && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <Buildings className="h-3.5 w-3.5" />
              Gebouwen
            </div>
            <div className="space-y-1.5">
              {filteredBuildings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Geen gebouwen gevonden
                </p>
              ) : (
                filteredBuildings.map((b) => {
                  const item: LocationItemData = {
                    id: `building-${b.id}`,
                    type: 'building',
                    label: b.id,
                    sublabel: b.company?.name,
                    buildingId: b.id,
                    instanceCount: instanceCounts.buildingCounts[b.id] || 0,
                  };
                  return (
                    <LocationItem
                      key={item.id}
                      item={item}
                      isChecked={selectedLocations.has(item.id)}
                      onToggle={(checked) => toggleSelection(item.id, checked)}
                    />
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Separator between sections */}
        {showBuildingSection && showUnitSection && (
          <div className="border-t my-2" />
        )}

        {/* UNIT / ROOM scope section */}
        {showUnitSection && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <House className="h-3.5 w-3.5" />
              Gebouwen & Units
            </div>
            <div className="space-y-1">
              {filteredBuildings.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Geen gebouwen gevonden
                </p>
              ) : (
                filteredBuildings.map((building) => {
                  const buildingUnits = getUnitsForBuilding(building.id);
                  const isExpanded = expandedBuildings.has(building.id);

                  // Check if all units in building are selected
                  const buildingUnitIds = buildingUnits.map((u) => `unit-${u.id}`);
                  const allBuildingUnitsSelected = buildingUnitIds.length > 0 &&
                    buildingUnitIds.every((id) => selectedLocations.has(id));
                  const someBuildingUnitsSelected = buildingUnitIds.some((id) => selectedLocations.has(id));
                  const selectedInBuilding = buildingUnitIds.filter((id) => selectedLocations.has(id)).length;

                  return (
                    <div key={building.id}>
                      {/* Building header */}
                      <div className="flex items-center gap-1.5 rounded-md px-2 py-1.5 hover:bg-accent/50 transition-colors">
                        {/* Select-all checkbox on the LEFT */}
                        {buildingUnits.length > 0 && (
                          <Checkbox
                            checked={allBuildingUnitsSelected ? true : someBuildingUnitsSelected ? 'indeterminate' : false}
                            onCheckedChange={() => {
                              setSelectedLocations((prev) => {
                                const next = new Set(prev);
                                if (allBuildingUnitsSelected) {
                                  buildingUnitIds.forEach((id) => next.delete(id));
                                } else {
                                  buildingUnitIds.forEach((id) => next.add(id));
                                }
                                return next;
                              });
                            }}
                            className="shrink-0"
                          />
                        )}

                        {/* Expand/collapse + building info */}
                        <button
                          onClick={() => toggleBuilding(building.id)}
                          className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        >
                          {isExpanded ? (
                            <CaretDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          ) : (
                            <CaretRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          )}
                          <Buildings className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium truncate">{building.id}</span>
                          {building.company?.name && (
                            <span className="text-xs text-muted-foreground truncate">
                              {building.company.name}
                            </span>
                          )}
                        </button>

                        {/* Right side: selected count or unit count */}
                        <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                          {selectedInBuilding > 0
                            ? `${selectedInBuilding} units geselecteerd`
                            : `${buildingUnits.length} units`}
                        </span>
                      </div>

                      {/* Expandable unit list */}
                      {isExpanded && (
                        <div className="ml-5 mt-1 mb-2 space-y-1">
                          {buildingUnits.length === 0 ? (
                            <p className="text-xs text-muted-foreground pl-2 py-1">
                              Geen units in dit gebouw
                            </p>
                          ) : (
                            buildingUnits.map((unit) => {
                              const item: LocationItemData = {
                                id: `unit-${unit.id}`,
                                type: 'unit',
                                label: unit.id,
                                sublabel: unit.unitType,
                                buildingId: building.id,
                                unitId: unit.id,
                                instanceCount: instanceCounts.unitCounts[unit.id] || 0,
                              };

                              return (
                                <LocationItem
                                  key={item.id}
                                  item={item}
                                  isChecked={selectedLocations.has(item.id)}
                                  onToggle={(checked) => toggleSelection(item.id, checked)}
                                />
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* PORTABLE section when combined with other scopes */}
        {scopes.includes(AssetScope.PORTABLE) && scopes.length > 1 && (
          <>
            <div className="border-t my-2" />
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5" />
                Draagbaar
              </div>
              <div className="text-center py-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onBulkCreate([{
                      id: 'portable',
                      type: 'building',
                      label: 'Draagbaar',
                      buildingId: '',
                      instanceCount: existingAssets.length,
                    }])
                  }
                  disabled={isCreating}
                >
                  {isCreating ? (
                    <SpinnerGap className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : null}
                  Draagbare voorziening aanmaken
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationSourcePanel;
